import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BellIcon, CheckIcon, HomeIcon, MessageSquareIcon, TagIcon, XIcon } from 'lucide-react';
import { Avatar } from './Avatar';
import { StatusBadge } from './StatusBadge';
import { BORROWER_NAME, teamMembers } from '../../data/conditions';
import { formatDateTime, getMember } from '../../utils/conditions';
import type { Condition } from '../../types/conditions';
interface CommentsDrawerProps {
  condition: Condition | null;
  onClose: () => void;
  onSubmit: (conditionId: string, body: string, mentions: string[], sentToBorrower: boolean) => void;
}
export function CommentsDrawer({
  condition,
  onClose,
  onSubmit
}: CommentsDrawerProps) {
  const [draft, setDraft] = useState('');
  const [tagged, setTagged] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [notified, setNotified] = useState<string[]>([]);
  const [sendToBorrower, setSendToBorrower] = useState(false);
  const [borrowerNotified, setBorrowerNotified] = useState(false);
  const listRef = useRef<HTMLOListElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setDraft('');
    setTagged([]);
    setNotified([]);
    setPickerOpen(false);
    setSendToBorrower(false);
    setBorrowerNotified(false);
  }, [condition?.id]);
  useEffect(() => {
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [condition?.comments.length]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (pickerOpen) setPickerOpen(false);else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, pickerOpen]);
  useEffect(() => {
    if (!pickerOpen) return;
    const onDown = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [pickerOpen]);
  function toggleTag(id: string) {
    setTagged(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
  }
  function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    if (!condition || !draft.trim()) return;
    onSubmit(condition.id, draft.trim(), tagged, sendToBorrower);
    setNotified(tagged);
    setBorrowerNotified(sendToBorrower);
    setDraft('');
    setTagged([]);
    setSendToBorrower(false);
    setPickerOpen(false);
  }
  return <AnimatePresence data-id="element-15" data-mp="3iz06yjhwbaqw">
      {condition && <>
          <motion.div className="fixed inset-0 z-40 bg-slate-900/20" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} aria-hidden="true" data-id="element-16" data-mp="3eusf4dg55eog" />
          <motion.aside role="dialog" aria-modal="true" aria-labelledby="comments-title" className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col bg-white shadow-2xl" initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 400,
        damping: 40
      }} data-id="element-17" data-mp="iymjxkz1fvzr">
            <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4" data-id="element-18" data-mp="11pnfdz0y99ek">
              <div className="min-w-0" data-id="element-19" data-mp="16mvzi55nrnig">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500" data-id="element-20" data-mp="16q1q5otxqxga">Condition comments</p>
                <h2 id="comments-title" className="mt-0.5 text-base font-semibold text-slate-900" data-id="element-21" data-mp="1anpq56ahjgf1">
                  {condition.name}
                </h2>
                <div className="mt-1.5 flex items-center gap-2" data-id="element-22" data-mp="3ci72d1l7t05z">
                  <StatusBadge status={condition.status} data-id="element-23" data-mp="3hfqjqad7lt25" />
                  <span className="truncate text-xs text-slate-500" data-id="element-24" data-mp="3hg8iper9aldr">{condition.description}</span>
                </div>
              </div>
              <button type="button" onClick={onClose} aria-label="Close comments" className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-25" data-mp="2h6z3cbfstndx">
                <XIcon className="h-5 w-5" data-id="element-26" data-mp="23eiz57b5vjbf" />
              </button>
            </header>

            {condition.comments.length === 0 ? <div className="flex flex-1 flex-col items-center justify-center px-8 text-center" data-id="element-27" data-mp="1yi2b0d3yp4yc">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100" data-id="element-28" data-mp="2d6gbwihtijc9">
                  <MessageSquareIcon className="h-5 w-5 text-slate-400" aria-hidden="true" data-id="element-29" data-mp="28d40ja25g91g" />
                </span>
                <p className="mt-3 text-sm font-semibold text-slate-800" data-id="element-30" data-mp="1kmhrbfgesipv">No comments yet</p>
                <p className="mt-1 text-sm text-slate-500" data-id="element-31" data-mp="1fnnagj20548e">
                  Add a note so the team knows where this stands. Tag a teammate when it's ready for them.
                </p>
              </div> : <ol ref={listRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-4" aria-label="Comment history" data-id="element-32" data-mp="3hf6zjf3dhcem">
                {condition.comments.map(c => {
            const author = getMember(c.authorId);
            return <li key={c.id} className="flex gap-3" data-id="element-33" data-mp="dyq3xfgcqxxi">
                      <Avatar member={author} size="sm" data-id="element-34" data-mp="ixta5382ja72" />
                      <div className="min-w-0 flex-1" data-id="element-35" data-mp="iv7272h9msob">
                        <p className="flex flex-wrap items-baseline gap-x-2 text-xs" data-id="element-36" data-mp="4wgw4xcgxint">
                          <span className="font-semibold text-slate-900" data-id="element-37" data-mp="8yc7kuo45cfb">{author.name}</span>
                          <span className="text-slate-500" data-id="element-38" data-mp="2pn7vs6jzp5bf">{author.role}</span>
                          <time className="text-slate-400" dateTime={c.createdAt} data-id="element-39" data-mp="2tpcpf5afu1y2">
                            {formatDateTime(c.createdAt)}
                          </time>
                        </p>
                        <div className={`mt-1 rounded-lg px-3 py-2 text-sm text-slate-800 ${c.sentToBorrower ? 'bg-amber-50 ring-1 ring-inset ring-amber-200' : 'bg-slate-50'}`} data-id="element-40" data-mp="v7iw6eckmqhf">
                          {c.sentToBorrower && <p className="mb-1 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-amber-800" data-id="element-41" data-mp="hh5omd3lddsm">
                              <HomeIcon className="h-3 w-3" aria-hidden="true" data-id="element-42" data-mp="cje0lu62ibtp" />
                              Sent to borrower
                            </p>}
                          <p className="whitespace-pre-wrap" data-id="element-43" data-mp="2koiq1e68rdg">{c.body}</p>
                        </div>
                        {c.mentions.length > 0 && <p className="mt-1.5 flex flex-wrap items-center gap-1 text-xs text-slate-500" data-id="element-44" data-mp="1wwmqs00qvh7r">
                            <TagIcon className="h-3 w-3" aria-hidden="true" data-id="element-45" data-mp="25cu45twgdn84" />
                            Tagged
                            {c.mentions.map(id => <span key={id} className="rounded bg-brand/10 px-1.5 py-0.5 font-medium text-brand-dark" data-id="element-46" data-mp="2e6gwtb5d6ad3">
                                {getMember(id).name}
                              </span>)}
                          </p>}
                      </div>
                    </li>;
          })}
              </ol>}

            <form onSubmit={handleSubmit} className="border-t border-slate-200 px-5 py-4" data-id="element-47" data-mp="20fbshvr1wtwf">
              {(notified.length > 0 || borrowerNotified) && <div className="mb-3 space-y-1 rounded-md bg-steel-light px-3 py-2 text-xs font-medium text-steel-dark" data-id="element-48" data-mp="36wywe26vptlr">
                  {notified.length > 0 && <p className="flex items-center gap-1.5" data-id="element-49" data-mp="tf373eidolq">
                      <BellIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-50" data-mp="3swoehhxbdswt" />
                      Notified {notified.map(id => getMember(id).name).join(', ')} by bell + email
                    </p>}
                  {borrowerNotified && <p className="flex items-center gap-1.5" data-id="element-51" data-mp="5qdsn8n897dl">
                      <HomeIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-52" data-mp="kfjtf11pr5nl" />
                      Sent to {BORROWER_NAME} via portal + email
                    </p>}
                </div>}

              <label className={`mb-2 flex cursor-pointer items-center justify-between gap-3 rounded-md border px-3 py-2 transition-colors ${sendToBorrower ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`} data-id="element-53" data-mp="tgrfmkqygg1b">
                <span className="flex items-center gap-2" data-id="element-54" data-mp="2qbe0ychwta77">
                  <HomeIcon className={`h-4 w-4 ${sendToBorrower ? 'text-amber-700' : 'text-slate-400'}`} aria-hidden="true" data-id="element-55" data-mp="15r8ytb4pe242" />
                  <span data-id="element-56" data-mp="wzqjf2fkzqc1">
                    <span className="block text-sm font-medium text-slate-800" data-id="element-57" data-mp="wy5kz1q5bn8w">Send to borrower</span>
                    <span className="block text-xs text-slate-500" data-id="element-58" data-mp="10zhhlr7ft8ql">
                      {sendToBorrower ? `${BORROWER_NAME} will see this in their portal + email` : 'Internal only — the borrower won’t see this'}
                    </span>
                  </span>
                </span>
                <input type="checkbox" role="switch" checked={sendToBorrower} onChange={e => setSendToBorrower(e.target.checked)} className="peer sr-only" data-id="element-59" data-mp="e2fn9qp8xd0t" />
                <span aria-hidden="true" className={`relative h-5 w-9 shrink-0 rounded-full transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-brand peer-focus-visible:ring-offset-1 ${sendToBorrower ? 'bg-amber-500' : 'bg-slate-300'}`} data-id="element-60" data-mp="2ceo0izrsep8x">
                  <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${sendToBorrower ? 'translate-x-[18px]' : 'translate-x-0.5'}`} data-id="element-61" data-mp="s9bt6279e9s9" />
                </span>
              </label>

              <label htmlFor="comment-input" className="sr-only" data-id="element-62" data-mp="16y890lfk4r3f">
                Add a comment
              </label>
              <textarea id="comment-input" rows={3} value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit(e);
          }} placeholder={sendToBorrower ? 'Write a message to the borrower…' : 'Add an internal comment…'} className={`w-full resize-none rounded-md border px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${sendToBorrower ? 'border-amber-300' : 'border-slate-300'}`} data-id="element-63" data-mp="1azaa77t1bd81" />

              {tagged.length > 0 && <ul className="mt-2 flex flex-wrap gap-1.5" aria-label="Tagged teammates" data-id="element-64" data-mp="35dpbqatlojve">
                  {tagged.map(id => {
              const m = getMember(id);
              return <li key={id} className="inline-flex items-center gap-1.5 rounded-full bg-brand/10 py-0.5 pl-0.5 pr-1.5 text-xs font-medium text-brand-dark" data-id="element-65" data-mp="3af4rpogx806x">
                        <Avatar member={m} size="sm" data-id="element-66" data-mp="2hpefxm2rhadz" />
                        {m.name}
                        <button type="button" onClick={() => toggleTag(id)} aria-label={`Remove ${m.name}`} className="rounded-full p-0.5 hover:bg-brand/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-67" data-mp="37feefscm28t7">
                          <XIcon className="h-3 w-3" aria-hidden="true" data-id="element-68" data-mp="3l845wcaws5xp" />
                        </button>
                      </li>;
            })}
                </ul>}

              <div className="mt-2 flex items-center justify-between gap-3" data-id="element-69" data-mp="2sj5fyt0jnzhu">
                <div ref={pickerRef} className="relative" data-id="element-70" data-mp="2xf41m8uhn3ek">
                  <button type="button" onClick={() => setPickerOpen(v => !v)} aria-haspopup="listbox" aria-expanded={pickerOpen} className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${pickerOpen || tagged.length > 0 ? 'border-brand bg-brand/5 text-brand' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`} data-id="element-71" data-mp="32frzneh4wkia">
                    <TagIcon className="h-4 w-4" aria-hidden="true" data-id="element-72" data-mp="1jgu58zexpwv2" />
                    Tag{tagged.length > 0 ? ` (${tagged.length})` : ''}
                  </button>

                  <AnimatePresence data-id="element-73" data-mp="1egfv9btrwcue">
                    {pickerOpen && <motion.div initial={{
                  opacity: 0,
                  y: 4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 4
                }} transition={{
                  duration: 0.12
                }} className="absolute bottom-full left-0 z-10 mb-1.5 w-72 rounded-md border border-slate-200 bg-white shadow-lg" data-id="element-74" data-mp="19jpjv1swy5fb">
                        <p className="border-b border-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-75" data-mp="36ytk77a6pbv3">
                          Tag teammates
                        </p>
                        <ul role="listbox" aria-multiselectable="true" aria-label="Teammates" className="py-1" data-id="element-76" data-mp="2wzkrehr223ab">
                          {teamMembers.map(m => {
                      const selected = tagged.includes(m.id);
                      return <li key={m.id} data-id="element-77" data-mp="2szkfb3rfi46f">
                                <button type="button" role="option" aria-selected={selected} onClick={() => toggleTag(m.id)} className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-slate-50 focus:bg-slate-50 focus:outline-none" data-id="element-78" data-mp="2t0c0dknegjig">
                                  <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${selected ? 'border-brand bg-brand text-white' : 'border-slate-300 bg-white'}`} aria-hidden="true" data-id="element-79" data-mp="1qn97noytu1fw">
                                    {selected && <CheckIcon className="h-3 w-3" data-id="element-80" data-mp="29jskao8ua7h7" />}
                                  </span>
                                  <Avatar member={m} size="sm" data-id="element-81" data-mp="24lh9aa6t5qxe" />
                                  <span className="min-w-0 flex-1" data-id="element-82" data-mp="2idnhna3pytma">
                                    <span className="block text-sm font-medium text-slate-800" data-id="element-83" data-mp="2ebses165486e">{m.name}</span>
                                    <span className="block text-xs text-slate-500" data-id="element-84" data-mp="2sbbmwtyybjip">{m.role}</span>
                                  </span>
                                </button>
                              </li>;
                    })}
                        </ul>
                        <div className="flex justify-end border-t border-slate-100 px-3 py-2" data-id="element-85" data-mp="1p0eb8tp87fmf">
                          <button type="button" onClick={() => setPickerOpen(false)} className="rounded-md bg-brand px-3 py-1 text-xs font-semibold text-white hover:bg-brand-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1" data-id="element-86" data-mp="1u00ov48yuxvr">
                            Done
                          </button>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                <button type="submit" disabled={!draft.trim()} className="rounded-md bg-brand px-3.5 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-brand-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300" data-id="element-87" data-mp="ic8szvh126of">
                  Post
                </button>
              </div>
              <p className="mt-2 flex items-center gap-1 text-xs text-slate-500" data-id="element-88" data-mp="62j7clgfssel">
                <BellIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-89" data-mp="147tlzn0txr3" />
                Tagged teammates get a bell + email notification
              </p>
            </form>
          </motion.aside>
        </>}
    </AnimatePresence>;
}